import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getWithdrawalTransactions,
  getMoreWithdrawalTransactions,
} from "redux/actions/transActions";
import moment from "moment";
import { formatNumberAmount } from "helpers/utilFunctions";
import { Box, Link, Text } from "@chakra-ui/react";
import { popLastId } from "helpers/utilFunctions";
import { DateTimeFormat } from "../../constants";
import { Table } from "antd";
import { Loader } from "components/common";
import { RootState } from "types";

const columns = [
  {
    dataIndex: "transactionAmount",
    key: "transactionAmount",
    render: (text: string) => (
      <Box>
        <Text fontSize={{ base: "xx-small", md: "xs" }} fontWeight="bold">
          {text}
        </Text>
        <Text fontSize={{ base: "xx-small" }} fontWeight="normal">
          Amount
        </Text>
      </Box>
    ),
  },
  {
    dataIndex: "transactionDate",
    key: "transactionDate",
    render: (text: string) => (
      <Box>
        <Text fontSize={{ base: "xx-small", md: "xs" }} fontWeight="bold">
          {text}
        </Text>
        <Text fontSize={{ base: "xx-small" }} fontWeight="normal">
          Date
        </Text>
      </Box>
    ),
  },
  {
    dataIndex: "narration",
    key: "narration",
    ellipsis: true,
    render: (text: string) => (
      <Box>
        <Text fontSize={{ base: "xx-small", md: "xs" }} fontWeight="bold">
          {text}
        </Text>
        <Text fontSize={{ base: "xx-small" }} fontWeight="normal">
          Narration
        </Text>
      </Box>
    ),
    className: "table-cell-display",
  },
  {
    dataIndex: "fee",
    key: "fee",
    render: (text: string) => (
      <Box>
        <Text fontSize={{ base: "xx-small", md: "xs" }} fontWeight="bold">
          {text}
        </Text>
        <Text fontSize={{ base: "xx-small" }} fontWeight="normal">
          Fee
        </Text>
      </Box>
    ),
    className: "table-cell-display table-cell-display--md",
  },
  {
    dataIndex: "accountNumber",
    key: "accountNumber",
    render: (text: string) => (
      <Box>
        <Text fontSize={{ base: "xx-small", md: "xs" }} fontWeight="bold">
          {text}
        </Text>
        <Text fontSize={{ base: "xx-small" }} fontWeight="normal">
          Account Number
        </Text>
      </Box>
    ),
    className: "table-cell-display table-cell-display--md",
  },
  {
    dataIndex: "accountName",
    key: "accountName",
    render: (text: string) => (
      <Box>
        <Text fontSize={{ base: "xx-small", md: "xs" }} fontWeight="bold">
          {text}
        </Text>
        <Text fontSize={{ base: "xx-small" }} fontWeight="normal">
          Account Name
        </Text>
      </Box>
    ),
    className: "table-cell-display table-cell-display--md",
  },
  {
    dataIndex: "bank",
    key: "bank",
    render: (text: string) => (
      <Box>
        <Text fontSize={{ base: "xx-small", md: "xs" }} fontWeight="bold">
          {text}
        </Text>
        <Text fontSize={{ base: "xx-small" }} fontWeight="normal">
          Bank
        </Text>
      </Box>
    ),
    className: "table-cell-display table-cell-display--md",
  },
  {
    dataIndex: "status",
    key: "status",
    render: (text: string) => (
      <Box>
        <Text fontSize={{ base: "xx-small", md: "xs" }} fontWeight="bold">
          {text}
        </Text>
        <Text fontSize={{ base: "xx-small" }} fontWeight="normal">
          Status
        </Text>
      </Box>
    ),
    className: "table-cell-display table-cell-display--md",
  },
];

export default function WithdrawalTransactionsTable() {
  const dispatch = useDispatch();
  const transactionState = useSelector(
    (state: RootState) => state.transactionReducer
  );
  const setting = useSelector((state: RootState) => state.settingsReducer);
  const userSetting = setting?.settingData?.data;
  const accountId = userSetting?.apiCredentials?.id;
  //61512e41ac43b4002907ca32
  const transactions = transactionState?.withdrawalTransactionsData?.data;
  const loading = transactionState?.requestLoading;
  const [lastIds, setLastIds] = useState<(string | null)[]>([null]);
  const xJwt = localStorage.getItem("tx_jwt");

  useEffect(() => {
    if (accountId) {
      dispatch(getWithdrawalTransactions(xJwt, accountId));
    }
  }, [dispatch, accountId, xJwt]);

  useEffect(() => {
    if (
      transactionState?.withdrawalTransactionsData !== undefined &&
      transactionState?.withdrawalTransactionsData?.lastId !== undefined
    ) {
      setLastIds([
        ...lastIds,
        transactionState?.withdrawalTransactionsData?.lastId,
      ]);
    }
  }, [transactionState?.withdrawalTransactionsData]); //eslint-disable-line

  const loadNextPage = () => {
    dispatch(
      getMoreWithdrawalTransactions(
        xJwt,
        accountId,
        transactionState?.withdrawalTransactionsData?.lastId
      )
    );
  };

  const loadPrevPage = () => {
    if (lastIds.length === 3) {
      dispatch(getWithdrawalTransactions(xJwt, accountId));
      setLastIds([null]);
    } else {
      dispatch(
        getMoreWithdrawalTransactions(
          xJwt,
          accountId,
          lastIds[lastIds.length - 3]
        )
      );
      popLastId(lastIds, setLastIds);
    }
  };

  const data: any[] | undefined = [];
  if (transactions) {
    const nonPendingTransactions = transactions.filter(
      (transaction: any) => transaction?.frontendStatus !== "pending"
    );
    for (let i = 0; i < nonPendingTransactions.length; i++) {
      const transaction = nonPendingTransactions[i];
      const key = transaction?._id;
      const transactionDate = moment(transaction?.createdAt).format(
        DateTimeFormat
      );
      const narration = transaction?.productsDetails?.narration;
      const transactionAmount = formatNumberAmount(transaction?.cost);
      const fee = transaction?.customerFee;
      const accountNumber =
        transaction?.deliveryLog?.beneficiary?.accountNumber;
      const accountName = transaction?.deliveryLog?.beneficiary?.accountName;
      const bank = transaction?.deliveryLog?.bankName;
      const status = transaction?.frontendStatus;

      data.push({
        key,
        transactionAmount,
        transactionDate,
        narration,
        fee,
        accountNumber,
        accountName,
        bank,
        status,
      });
    }
  }

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            defaultPageSize: 50,
            hideOnSinglePage: true,
            position: ["bottomLeft"],
          }}
        />
      )}
      <Box className="pagination-items">
        {lastIds.length <= 2 ? null : (
          <Link onClick={loadPrevPage} className="pagination-item-link prev">
            Previous page
          </Link>
        )}
        {!transactionState?.withdrawalTransactionsData?.isLastPage ? (
          <Link onClick={loadNextPage} className="pagination-item-link next">
            Next page
          </Link>
        ) : null}
      </Box>
    </>
  );
}
